import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Skull, Sword, Zap, Sparkles, Pill, Wind, ArrowLeft, Flame } from "lucide-react";
import { SFX } from "@/lib/sounds";
import { classChibi } from "@/lib/classChibi";
import { localizedString } from "@/lib/localized";
import { pickBark } from "@/lib/barks";
import { StatusEffectStrip } from "./StatusEffectStrip";
import { CombatLogRibbon } from "./CombatLogRibbon";
import "@/gm/gm-animations.css";

/**
 * Final-Fantasy-style turn-based combat overlay.
 *
 * Layout:
 *   ┌ enemy stage (left) ─── vs. ─── party (right) ┐
 *   │  large sprites, HP bars, name banners        │
 *   ├──────── combat log ribbon ───────────────────┤
 *   └── command bar: Attack · Ability · Item · Flee┘
 *
 * The Ability menu pulls from /api/class-skills and disables entries the
 * player can't afford or hasn't unlocked. Item menu reads consumables from
 * inventory. All actions route through /combat/action with the appropriate
 * payload (skill_id / item_id).
 */
export default function CombatOverlay({ ch, setCh, onVictory }) {
  const { t, i18n } = useTranslation();
  // Overlay any Gemini-produced translation onto the enemy name/flavor for
  // display; the source doc still ships in the DB verbatim, so combat/log
  // math is unaffected.
  const enemyRaw = ch?.combat?.enemy || {};
  const enemyName = localizedString(enemyRaw, "name", i18n.language) || enemyRaw.name;
  const enemyFlavor = localizedString(enemyRaw, "flavor", i18n.language) || enemyRaw.flavor;
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState("root");   // root | ability | item
  const [book, setBook] = useState(null);      // class-skills response
  const [party, setParty] = useState(null);    // /party/mine payload
  const [floaters, setFloaters] = useState([]);// damage popups
  const [shake, setShake] = useState(null);    // 'enemy' | 'player' | null
  const [bark, setBark] = useState(null);      // { text, id } - shout above chibi

  const combat = ch.combat;
  const cls = ch.chosen_class || ch.class || "Warrior";
  const chibi = classChibi(cls, ch.appearance?.gender, ch.appearance?.class_image);
  const classAnimations = Array.isArray(ch.appearance?.class_animations) ? ch.appearance.class_animations : [];

  useEffect(() => {
    if (!combat) return;
    api.classSkills().then(setBook).catch(() => setBook({ skills: [] }));
    api.partyMine().then(setParty).catch(() => setParty(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combat?.enemy?.name]);

  if (!combat) return null;
  const enemy = combat.enemy;
  const enemyPct = Math.max(0, (enemy.hp / enemy.max_hp) * 100);
  const playerPct = Math.max(0, (ch.hp / ch.max_hp) * 100);

  // Identify a party companion (the other member of the 2-hero party, if any).
  const partner =
    party && Array.isArray(party.members_info)
      ? party.members_info.find(m => m.user_id !== undefined && m.name !== ch.name) || null
      : null;

  const popFloater = (target, text, tint) => {
    const id = Math.random().toString(36).slice(2, 8);
    setFloaters(f => [...f, { id, target, text, tint }]);
    setTimeout(() => setFloaters(f => f.filter(x => x.id !== id)), 900);
  };

  const shakeFor = (who) => {
    setShake(who);
    setTimeout(() => setShake(null), 260);
  };

  // Emit a class-appropriate bark over the player chibi for ~1.6s.
  const barkOnce = (action) => {
    const text = pickBark(action, cls);
    const id = Math.random().toString(36).slice(2, 8);
    setBark({ text, id });
    setTimeout(() => setBark(b => (b && b.id === id ? null : b)), 1600);
  };

  const send = async (action, opts = {}) => {
    if (busy) return;
    setBusy(true);
    // Fire the bark synchronously so it appears before the server responds.
    barkOnce(action);
    try {
      const prevEnemyHp = enemy.hp;
      const prevPlayerHp = ch.hp;
      const res = await api.combat(action, opts.item_id || null, opts.skill_id || null);

      // Play FX per action
      if (action === "attack" || action === "special") SFX.sword();
      else if (action === "magic") SFX.magic();
      else if (action === "ability") {
        // Skill-scoped SFX (mana = magic tone, vigor = sword tone)
        const pool = opts.pool || "vigor";
        pool === "mana" ? SFX.magic() : SFX.sword();
      } else if (action === "flee") SFX.step();
      else if (action === "item") SFX.loot();

      setCh(res.character);
      setMenu("root");

      // Damage floaters based on HP deltas
      const newEnemyHp = res.character?.combat?.enemy?.hp ?? 0;
      const dealt = Math.max(0, prevEnemyHp - newEnemyHp);
      if (dealt > 0) { popFloater("enemy", `-${dealt}`, "#E55A00"); shakeFor("enemy"); }
      const newPlayerHp = res.character?.hp ?? ch.hp;
      const taken = Math.max(0, prevPlayerHp - newPlayerHp);
      if (taken > 0) { popFloater("player", `-${taken}`, "#8A0303"); shakeFor("player"); SFX.hurt(); barkOnce("hurt"); }
      const gained = Math.max(0, newPlayerHp - prevPlayerHp);
      if (gained > 0) { popFloater("player", `+${gained}`, "#6E8B3D"); }

      if (res.result.ended) {
        if (res.result.victory) {
          SFX.victory();
          // Notify parent so it can render VictorySplash even after this
          // overlay unmounts (Game.jsx pulls it out of the tree when combat
          // is cleared from the character document).
          onVictory && onVictory({
            xp: res.result.xp,
            gold: res.result.gold,
            drop: res.result.drop || null,
            enemy_name: enemy.name,
          });
        } else if (res.result.fled) {
          toast("You escaped into the fog.");
        } else if (res.result.defeated) {
          toast.error("You were defeated. -10 gold.");
        }
      }
    } catch (e) {
      SFX.denied();
      toast.error(e?.response?.data?.detail || "Combat failed");
    } finally { setBusy(false); }
  };

  const potions = (ch.inventory || []).filter(i => i.slot === "consumable");
  const abilities = (book?.skills || []).filter(s => s.unlocked);

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      data-testid="combat-overlay"
      style={{
        background:
          "radial-gradient(80% 60% at 50% 40%, rgba(60,10,10,0.55), rgba(5,5,5,0.95)), " +
          "radial-gradient(60% 40% at 20% 30%, rgba(90,0,0,0.45), transparent 70%), " +
          "radial-gradient(60% 40% at 80% 70%, rgba(20,20,60,0.4), transparent 70%), #050505",
      }}
    >
      {/* Ambient torch flicker layer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "repeating-radial-gradient(circle at 15% 20%, rgba(229,90,0,0.05) 0 6px, transparent 6px 12px), " +
            "repeating-radial-gradient(circle at 85% 80%, rgba(74,123,199,0.05) 0 6px, transparent 6px 12px)",
          animation: "ember-pulse 4s ease-in-out infinite",
        }}
      />

      <div className="relative h-full w-full max-w-[1024px] mx-auto flex flex-col">
        {/* Header banner */}
        <div className="pt-4 px-4 flex items-center justify-between">
          <div className="font-heading tracking-[0.4em] text-[10px] text-[#8A0303] uppercase">Combat</div>
          <div className="font-mono text-[10px] text-[#666] tracking-widest uppercase">Turn-based</div>
        </div>

        {/* Stage: enemy left, player right */}
        <div className="flex-1 grid grid-cols-2 gap-4 items-center px-6 pt-2 pb-4">
          {/* Enemy */}
          <div className="flex flex-col items-center gap-2 relative" data-testid="combat-stage-enemy">
            {enemy.is_elite && (
              <div
                data-testid="elite-badge"
                className="font-heading text-[10px] tracking-[0.4em] uppercase text-[#E5C34A]"
                style={{ textShadow: "0 0 6px #E5C34A88, 0 0 2px #000" }}
              >
                ✦ Elite ✦
              </div>
            )}
            <NameBanner name={enemyName} tone={enemy.is_elite ? "#E5C34A" : "#8A0303"} />
            <div
              className="relative"
              style={{ width: 200, height: 260 }}
              data-shake={shake === "enemy" ? "1" : "0"}
            >
              <EnemySprite enemy={enemy} shaking={shake === "enemy"} />
              {floaters.filter(f => f.target === "enemy").map(f => (
                <span
                  key={f.id}
                  className="absolute font-heading font-bold text-2xl pointer-events-none"
                  style={{
                    left: "50%", top: "20%",
                    transform: "translate(-50%, -50%)",
                    color: f.tint,
                    textShadow: "0 0 8px rgba(0,0,0,0.9), 0 0 4px #000",
                    animation: "fade-up 0.9s ease-out forwards",
                  }}
                >
                  {f.text}
                </span>
              ))}
            </div>
            <HpBar current={enemy.hp} max={enemy.max_hp} pct={enemyPct} tone="#8A0303" label={`${enemy.hp}/${enemy.max_hp}`} testid="enemy-hp" />
            <div className="font-mono text-[10px] text-[#666] tracking-widest uppercase">
              ATK {enemy.atk} · DEF {enemy.def}
            </div>
            <StatusEffectStrip effects={enemy.status_effects} align="center" />
          </div>

          {/* Player + optional party companion */}
          <div className="flex flex-col items-center gap-2 relative" data-testid="combat-stage-player">
            <div className="flex items-end justify-center gap-2 w-full">
              {/* Player */}
              <div className="flex flex-col items-center gap-1 relative">
                {partner && (
                  <div
                    aria-hidden
                    data-testid="party-buff-aura"
                    className="absolute pointer-events-none"
                    style={{
                      left: "50%",
                      top: "40%",
                      transform: "translate(-50%, -50%)",
                      width: 180, height: 220,
                      borderRadius: "50%",
                      background: "radial-gradient(50% 50% at 50% 50%, rgba(229,195,74,0.35), rgba(229,195,74,0.10) 55%, transparent 75%)",
                      animation: "party-aura 2.4s ease-in-out infinite",
                      filter: "blur(4px)",
                      zIndex: 0,
                    }}
                  />
                )}
                <NameBanner name={ch.name} tone="#E5C34A" small={!!partner} />
                <div
                  className="relative"
                  style={{ width: partner ? 150 : 200, height: partner ? 210 : 260 }}
                  data-shake={shake === "player" ? "1" : "0"}
                >
                  <PlayerSprite chibi={chibi} cls={cls} shaking={shake === "player"} animations={classAnimations} />
                  {bark && (
                    <div
                      key={bark.id}
                      data-testid="combat-bark"
                      className="absolute pointer-events-none"
                      style={{
                        left: "50%",
                        top: "-10px",
                        transform: "translate(-50%, -100%)",
                        animation: "bark-pop 1.6s ease-out forwards",
                      }}
                    >
                      <div
                        className="relative px-2 py-1 border border-[#E5C34A] font-heading text-[10px] tracking-widest uppercase text-[#E5C34A] whitespace-nowrap"
                        style={{
                          background: "rgba(5,5,5,0.92)",
                          boxShadow: "0 0 8px rgba(229,195,74,0.35)",
                        }}
                      >
                        {bark.text}
                        {/* Speech tail */}
                        <span
                          aria-hidden
                          className="absolute"
                          style={{
                            left: "50%",
                            bottom: -6,
                            transform: "translateX(-50%) rotate(45deg)",
                            width: 8,
                            height: 8,
                            background: "rgba(5,5,5,0.92)",
                            borderRight: "1px solid #E5C34A",
                            borderBottom: "1px solid #E5C34A",
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {floaters.filter(f => f.target === "player").map(f => (
                    <span
                      key={f.id}
                      className="absolute font-heading font-bold text-2xl pointer-events-none"
                      style={{
                        left: "50%", top: "20%",
                        transform: "translate(-50%, -50%)",
                        color: f.tint,
                        textShadow: "0 0 8px rgba(0,0,0,0.9), 0 0 4px #000",
                        animation: "fade-up 0.9s ease-out forwards",
                      }}
                    >
                      {f.text}
                    </span>
                  ))}
                </div>
                <HpBar current={ch.hp} max={ch.max_hp} pct={playerPct} tone="#6E8B3D" label={`${ch.hp}/${ch.max_hp}`} testid="player-hp" width={partner ? 150 : 200} />
                <div className="flex gap-3 font-mono text-[10px] tracking-widest uppercase">
                  {(ch.max_vigor || 0) > 0 && (
                    <span className="text-[#B89C3A]" data-testid="player-vigor">VGR {ch.vigor}/{ch.max_vigor}</span>
                  )}
                  {(ch.max_mana || 0) > 0 && (
                    <span className="text-[#4A7BC7]" data-testid="player-mana">MNA {ch.mana}/{ch.max_mana}</span>
                  )}
                </div>
                <StatusEffectStrip effects={ch.status_effects} align="center" />
              </div>

              {/* Party companion (if any) */}
              {partner && (
                <div className="flex flex-col items-center gap-1 relative" data-testid="combat-stage-partner">
                  <div
                    aria-hidden
                    className="absolute pointer-events-none"
                    style={{
                      left: "50%",
                      top: "40%",
                      transform: "translate(-50%, -50%)",
                      width: 150, height: 190,
                      borderRadius: "50%",
                      background: "radial-gradient(50% 50% at 50% 50%, rgba(229,195,74,0.35), rgba(229,195,74,0.10) 55%, transparent 75%)",
                      animation: "party-aura 2.6s ease-in-out infinite",
                      filter: "blur(4px)",
                      zIndex: 0,
                    }}
                  />
                  <NameBanner name={partner.name} tone="#B44BE0" small />
                  <div className="relative" style={{ width: 130, height: 190 }}>
                    <PlayerSprite
                      chibi={classChibi(partner.class, partner.appearance?.gender, partner.appearance?.class_image)}
                      cls={partner.class}
                      shaking={false}
                    />
                  </div>
                  <HpBar
                    current={partner.hp || 0}
                    max={partner.max_hp || 1}
                    pct={Math.max(0, ((partner.hp || 0) / (partner.max_hp || 1)) * 100)}
                    tone="#B44BE0"
                    label={`${partner.hp || 0}/${partner.max_hp || 1}`}
                    testid="partner-hp"
                    width={130}
                  />
                  <div className="font-mono text-[9px] text-[#8c8c8c] tracking-widest uppercase">
                    Lv {partner.level} · {partner.class}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Combat log ribbon (with verbosity switcher: basic/detailed/advanced) */}
        <CombatLogRibbon ch={ch} setCh={setCh} entries={ch.adventure_log || []} />

        {/* Command bar */}
        <div className="p-3 border-t border-[#262626] bg-[#080606]" data-testid="combat-command-bar">
          {menu === "root" && (
            <div className="grid grid-cols-4 gap-2">
              <CmdBtn testid="combat-attack" Icon={Sword}   label="Attack"  disabled={busy} onClick={() => send("attack")} />
              <CmdBtn testid="combat-ability-open" Icon={Sparkles} label="Ability" disabled={busy || abilities.length === 0} onClick={() => setMenu("ability")} />
              <CmdBtn testid="combat-item-open" Icon={Pill} label="Item" disabled={busy || potions.length === 0} onClick={() => setMenu("item")} />
              <CmdBtn testid="combat-flee" Icon={Wind} label="Flee" disabled={busy} onClick={() => send("flee")} tone="#8A0303" />
            </div>
          )}

          {menu === "ability" && (
            <div data-testid="combat-menu-ability">
              <MenuHead label={`${book?.class || cls} · Spellbook`} onBack={() => setMenu("root")} />
              {(book?.skills || []).length === 0 && (
                <div className="text-center text-[#666] text-xs italic py-4">
                  You haven't learned any class abilities yet.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
                {(book?.skills || []).map(s => {
                  const cur = int(ch[s.cost_pool]);
                  const cap = int(ch["max_" + s.cost_pool]);
                  const affordable = s.unlocked && cap > 0 && cur >= s.cost;
                  return (
                    <button
                      key={s.id}
                      data-testid={`combat-ability-${s.id}`}
                      onClick={() => affordable && send("ability", { skill_id: s.id, pool: s.cost_pool })}
                      disabled={busy || !affordable}
                      className={`text-left p-2 border transition-colors ${
                        s.unlocked
                          ? affordable
                            ? "border-[#333] hover:border-[#E55A00]"
                            : "border-[#222] opacity-50"
                          : "border-[#1a1a1a] opacity-30"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] truncate">
                          {s.unlocked ? s.name : `?? Lv ${s.unlock}`}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: s.cost_pool === "mana" ? "#4A7BC7" : "#B89C3A" }}>
                          {s.cost} {s.cost_pool}
                        </span>
                      </div>
                      {s.unlocked && (
                        <div className="font-body text-[10px] text-[#8c8c8c] italic mt-0.5 line-clamp-2">
                          {s.desc}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {menu === "item" && (
            <div data-testid="combat-menu-item">
              <MenuHead label={t("combat.consumables")} onBack={() => setMenu("root")} />
              <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
                {potions.map(p => (
                  <button
                    key={p.id}
                    data-testid={`combat-item-${p.id}`}
                    disabled={busy}
                    onClick={() => send("item", { item_id: p.id })}
                    className="p-2 border border-[#333] hover:border-[#6E8B3D] text-left"
                  >
                    <div className="flex items-center gap-1">
                      <Pill className="w-3 h-3 text-[#6E8B3D]" strokeWidth={1.5} />
                      <span className="text-xs truncate text-[#E2DFD8]">{p.name}</span>
                    </div>
                    <div className="font-mono text-[9px] text-[#6E8B3D]">
                      {p.heal ? `+${p.heal} HP ` : ""}
                      {p.mana ? `+${p.mana} MP ` : ""}
                      {p.vigor ? `+${p.vigor} VGR` : ""}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        [data-shake="1"] { animation: cb-shake 0.26s linear; }
        @keyframes cb-shake {
          0%,100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          50% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
        }
        @keyframes cb-float {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-6px); }
        }
        @keyframes cb-enemy-hover {
          0%,100% { transform: translateY(0) scale(1); }
          50%     { transform: translateY(-10px) scale(1.02); }
        }
        @keyframes party-aura {
          0%,100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
          50%     { opacity: 0.9;  transform: translate(-50%, -50%) scale(1.08); }
        }
        @keyframes elite-aura {
          0%,100% { opacity: 0.7; }
          50%     { opacity: 1; }
        }
        @keyframes bark-pop {
          0%   { opacity: 0; transform: translate(-50%, -80%) scale(0.6); }
          15%  { opacity: 1; transform: translate(-50%, -110%) scale(1.05); }
          85%  { opacity: 1; transform: translate(-50%, -105%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -95%) scale(0.95); }
        }
      `}</style>
    </div>
  );
}

function int(v) { const n = parseInt(v, 10); return Number.isFinite(n) ? n : 0; }

function NameBanner({ name, tone, small }) {
  return (
    <div
      className={`px-2 py-0.5 font-heading tracking-widest uppercase border truncate ${small ? "text-[10px] max-w-[140px]" : "text-sm max-w-[200px] px-3 py-1"}`}
      style={{ color: tone, borderColor: tone, background: `${tone}12` }}
      data-testid={`name-banner-${name}`}
    >
      {name}
    </div>
  );
}

function HpBar({ pct, tone, label, testid, width = 200 }) {
  return (
    <div style={{ width }}>
      <div className="vbar h-2" data-testid={testid}>
        <span style={{ width: `${pct}%`, background: tone }} />
      </div>
      <div className="font-mono text-[10px] text-[#8c8c8c] tracking-widest uppercase text-center mt-0.5">{label}</div>
    </div>
  );
}

const GM_ANIMATIONS = {
  "idle-float": ["gm-float", "3.4s", "ease-in-out"],
  "idle-pulse": ["gm-pulse", "2.2s", "ease-in-out"],
  "idle-breathe": ["gm-breathe", "3.8s", "ease-in-out"],
  "idle-wobble": ["gm-wobble", "4.5s", "ease-in-out"],
  "hover-stretch": ["gm-auto-stretch", "2.8s", "ease-in-out"],
  "hover-compress": ["gm-auto-compress", "2.8s", "ease-in-out"],
  "hover-rotate": ["gm-auto-rotate", "3s", "ease-in-out"],
  "hover-glow": ["gm-auto-glow", "2.4s", "ease-in-out"],
  "hover-shake": ["gm-auto-shake", "3.5s", "ease-in-out"],
};

function gmAnimationClasses(animations) {
  if (!Array.isArray(animations)) return "";
  const list = animations.length ? animations : ["idle-float"];
  return list.map(a => `gm-anim-${a}`).join(" ");
}

function gmAnimationStyle(animations) {
  if (!Array.isArray(animations)) return null;
  const list = animations.length ? animations : ["idle-float"];
  return { animation: list.map(a => {
    const [name, duration, easing] = GM_ANIMATIONS[a] || GM_ANIMATIONS["idle-float"];
    return `${name} ${duration} ${easing} infinite`;
  }).join(", ") };
}

function PlayerSprite({ chibi, cls, shaking, animations = undefined }) {
  const gmAnims = gmAnimationClasses(animations);
  const gmStyle = gmAnimationStyle(animations);
  const hasGmAnimation = !!gmAnims;
  return (
    <div
      className={`w-full h-full flex items-end justify-center ${gmAnims}`}
      style={{ ...(gmStyle || {}), animation: shaking ? undefined : (gmStyle?.animation || "cb-float 3.6s ease-in-out infinite") }}
    >
      {chibi ? (
        <img
          src={chibi}
          alt={`${cls} sprite`}
          draggable={false}
          className="pointer-events-none select-none"
          style={{
            height: "100%",
            width: "auto",
            objectFit: "contain",
            filter: "drop-shadow(0 6px 6px rgba(0,0,0,0.75))",
          }}
        />
      ) : (
        <div
          className="w-32 h-40 border-2 border-[#E5C34A] flex items-center justify-center bg-[#0a0a0a]"
        >
          <span className="font-heading tracking-widest text-[#E5C34A]">{cls}</span>
        </div>
      )}
    </div>
  );
}

function EnemySprite({ enemy, shaking }) {
  const gmAnims = gmAnimationClasses(enemy?.animations);
  const gmStyle = gmAnimationStyle(enemy?.animations);
  const hasGmAnimation = !!gmAnims;
  // GM artwork is the primary visual. Keep the procedural sprite as a
  // backwards-compatible fallback for enemies without an image.
  if (enemy?.image) {
    return (
      <div
        className={`w-full h-full relative flex items-center justify-center overflow-hidden ${gmAnims}`}
        style={{ ...(gmStyle || {}), animation: shaking ? undefined : (gmStyle?.animation || "cb-enemy-hover 4.2s ease-in-out infinite") }}
        data-elite={enemy?.is_elite ? "1" : "0"}
        data-testid="enemy-gm-image"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: enemy?.is_elite ? "radial-gradient(50% 50%, rgba(229,195,74,.35), transparent 70%)" : "radial-gradient(50% 50%, rgba(229,90,0,.20), transparent 70%)", filter: "blur(8px)" }}
        />
        <img
          src={enemy.image}
          alt={enemy.name || "Enemy"}
          draggable={false}
          className="relative z-10 max-w-full max-h-full object-contain select-none pointer-events-none"
          style={{ filter: shaking ? "brightness(1.25)" : "drop-shadow(0 8px 10px rgba(0,0,0,.75))" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
    );
  }

  // Procedural enemy visual: dark radial + skull + tint by name hash.
  const nameHash = (enemy?.name || "").split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 5381);
  const hue = nameHash % 360;
  const tint = enemy?.is_elite ? "#E5C34A" : `hsl(${hue}, 45%, 35%)`;
  const auraTint = enemy?.is_elite ? "#E5C34A" : tint;
  return (
    <div
      className={`w-full h-full relative flex items-end justify-center ${gmAnims}`}
      style={{ ...(gmStyle || {}), animation: shaking ? undefined : (gmStyle?.animation || "cb-enemy-hover 4.2s ease-in-out infinite") }}
      data-elite={enemy?.is_elite ? "1" : "0"}
    >
      {/* Aura — elites get a gold-crowned glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(50% 40% at 50% 60%, ${auraTint}66, transparent 70%)`,
          filter: enemy?.is_elite ? "blur(3px)" : "blur(2px)",
          animation: enemy?.is_elite ? "elite-aura 2.6s ease-in-out infinite" : undefined,
        }}
      />
      {/* Silhouette body */}
      <div
        className="absolute"
        style={{
          left: "50%", bottom: "8%",
          transform: "translateX(-50%)",
          width: 160, height: 190,
          background: `linear-gradient(180deg, #050505 0%, ${tint} 55%, #050505 100%)`,
          clipPath: "polygon(30% 0, 70% 0, 82% 30%, 90% 65%, 78% 100%, 22% 100%, 10% 65%, 18% 30%)",
          boxShadow: `0 0 18px ${tint}, inset 0 0 20px rgba(0,0,0,0.7)`,
        }}
      />
      {/* Head skull */}
      <Skull
        className="absolute"
        style={{
          left: "50%", top: "15%",
          transform: "translateX(-50%)",
          width: 96, height: 96,
          color: "#0a0a0a",
          filter: `drop-shadow(0 0 4px ${tint})`,
        }}
        strokeWidth={1.2}
      />
      {/* Glowing eyes */}
      <div className="absolute" style={{ left: "42%", top: "26%", width: 6, height: 6, background: "#E55A00", boxShadow: "0 0 8px #E55A00, 0 0 4px #E55A00" }} />
      <div className="absolute" style={{ left: "56%", top: "26%", width: 6, height: 6, background: "#E55A00", boxShadow: "0 0 8px #E55A00, 0 0 4px #E55A00" }} />
      {/* Weapon hint (blade) */}
      <div className="absolute" style={{ left: "20%", bottom: "12%", width: 3, height: 90, background: "#8A0303", boxShadow: "0 0 4px #8A0303" }} />
    </div>
  );
}

function CmdBtn({ testid, Icon, label, onClick, disabled, tone = "#E55A00" }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="py-3 px-2 border flex flex-col items-center justify-center gap-1 font-heading tracking-widest text-[10px] uppercase text-[#E2DFD8] disabled:opacity-30 transition-colors"
      style={{ borderColor: disabled ? "#333" : tone, background: disabled ? "transparent" : `${tone}10` }}
    >
      <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: tone }} />
      {label}
    </button>
  );
}

function MenuHead({ label, onBack }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-2">
      <button
        data-testid="combat-menu-back"
        onClick={onBack}
        className="flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] hover:text-[#E2DFD8]"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> {t("combat.back")}
      </button>
      <span className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A]">
        <Flame className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
        {label}
      </span>
      <span className="w-10" />
    </div>
  );
}
